import React, { useEffect, useRef, useState } from "react";
import UtilityHelper from "../../../service/utilities/UtilityHelper";

type BottomSheetType = {
  className?: string;
  children: React.ReactNode;
};

const BottomSheet: React.FC<BottomSheetType> = ({
  children,
  className = "",
}) => {
  const maxHeight = 320;
  const sheetRef = useRef<HTMLDivElement>(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const isDragging = useRef(false);

  const [translateY, setTranslateY] = useState(maxHeight); // closed position
  const MIN_Y = 0; // fully open
  const MAX_Y = maxHeight; // fully closed

  const handleStart = (clientY: number) => {
    isDragging.current = true;
    startY.current = clientY;
    currentY.current = translateY;
  };

  const handleMove = (clientY: number) => {
    if (!isDragging.current) return;

    const diff = clientY - startY.current;
    let newY = currentY.current + diff;

    // limit drag range
    newY = Math.max(MIN_Y, Math.min(MAX_Y, newY));

    setTranslateY(newY);
  };

  const handleEnd = () => {
    isDragging.current = false;

    // snap open or closed
    if (translateY < MAX_Y / 2) {
      setTranslateY(MIN_Y); // open
    } else {
      setTranslateY(MAX_Y); // close
    }
  };

  // Mouse events
  const onMouseDown = (e: React.MouseEvent) => handleStart(e.clientY);
  const onMouseMove = (e: MouseEvent) => handleMove(e.clientY);
  const onMouseUp = () => handleEnd();

  // Touch events
  const onTouchStart = (e: React.TouchEvent) =>
    handleStart(e.touches[0].clientY);
  const onTouchMove = (e: TouchEvent) => handleMove(e.touches[0].clientY);
  const onTouchEnd = () => handleEnd();

  // Attach global listeners
  useEffect(() => {
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    window.addEventListener("touchmove", onTouchMove);
    window.addEventListener("touchend", onTouchEnd);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);

      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
    // eslint-disable-next-line
  }, [translateY]);

  return (
    <div
      ref={sheetRef}
      className="bgcolor-white"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        height: maxHeight + 150,
        boxShadow: "0 -4px 15px rgba(0,0,0,0.2)",
        borderRadius: "16px 16px 0 0",
        transform: `translateY(${translateY}px)`,
        transition: isDragging.current ? "none" : "transform 0.25s ease",
        touchAction: "none",
      }}
    >
      {/* Drag handle */}
      <div
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        className="w-100 d-center pb-2"
      >
        <div
          style={{
            width: 50,
            height: 6,
            background: "#ccc",
            borderRadius: 10,
            margin: "10px auto",
            cursor: "grab",
          }}
        />
      </div>

      <div
        className={`${
          UtilityHelper.isEmpty(className) ? "px-4" : className
        }  pt-1 pb-2`}
        style={{ height: `calc(100% - 55px)` }}
      >
        {children}
      </div>
    </div>
  );
};

export default BottomSheet;
